import React, { useState, useEffect, useRef, useCallback, useSyncExternalStore } from 'react'
import { ChevronLeft, ChevronRight, X, Waves, Mountain, Anchor, Camera, Utensils, Wind } from 'lucide-react'

const CATEGORY_ICONS = { Water: Waves, Adventure: Mountain, Cultural: Anchor, Photo: Camera, Dining: Utensils, Wellness: Wind }
const MOCK_COLORS = ['#b8ccd8','#a8d4c0','#d4c4a8','#c8b8d8','#b8d4c8','#d4b8b8','#a8c8d4','#c4d4a8']

const p = (seed) => `https://picsum.photos/seed/${seed}/800/500`

const MOCK = [
  { title: 'Kayaking in Luon Cave',     category: 'Water',     images: [p('kayak1'),p('kayak2'),p('kayak3')],   description: 'Paddle through the iconic limestone arch into a hidden lagoon teeming with life.' },
  { title: 'Sung Sot Cave Exploration', category: 'Adventure', images: [p('cave1'),p('cave2'),p('cave3')],      description: 'Discover one of Halong Bay\'s largest and most spectacular grottoes.' },
  { title: 'Vietnamese Cooking Class',  category: 'Dining',    images: [p('cook1'),p('cook2'),p('cook3')],      description: 'Learn to prepare traditional dishes with our onboard chef.' },
  { title: 'Tai Chi at Sunrise',        category: 'Wellness',  images: [p('taichi1'),p('taichi2'),p('taichi3')], description: 'Greet the morning mist with a guided session on the sun deck.' },
  { title: 'Pearl Farm Visit',          category: 'Cultural',  images: [p('pearl1'),p('pearl2'),p('pearl3')],   description: 'Witness the art of Halong pearl cultivation up close.' },
  { title: 'Photography Tour',          category: 'Photo',     images: [p('photo1'),p('photo2'),p('photo3')],   description: 'Capture the bay\'s best angles with guidance from a local photographer.' },
  { title: 'Night Squid Fishing',       category: 'Water',     images: [p('squid1'),p('squid2'),p('squid3')],   description: 'Cast lines under the stars on a traditional Vietnamese fishing boat.' },
  { title: 'Ti Top Island Hike',        category: 'Adventure', images: [p('titop1'),p('titop2'),p('titop3')],   description: 'Climb to the panoramic viewpoint overlooking 3,000 limestone islets.' },
]

function ActivityModal({ item, colorIdx, onClose }) {
  const [imgIdx, setImgIdx] = useState(0)
  const hasImages = item.images?.length > 0
  const Icon = CATEGORY_ICONS[item.category] ?? Anchor

  const prev = useCallback(() => setImgIdx(i => (i - 1 + item.images.length) % item.images.length), [item.images.length])
  const next = useCallback(() => setImgIdx(i => (i + 1) % item.images.length), [item.images.length])

  useEffect(() => {
    const onKey = e => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowLeft'  && hasImages) prev()
      if (e.key === 'ArrowRight' && hasImages) next()
    }
    window.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = '' }
  }, [prev, next, onClose, hasImages])

  return (
    <div className="sc-act-modal__backdrop" onClick={onClose}>
      <div className="sc-act-modal" onClick={e => e.stopPropagation()}>
        <button className="sc-act-modal__close" onClick={onClose} aria-label="Close"><X size={18} /></button>

        {/* Image area */}
        <div
          className="sc-act-modal__img-wrap"
          style={{ background: hasImages ? undefined : MOCK_COLORS[colorIdx % MOCK_COLORS.length] }}
        >
          {hasImages ? (
            <>
              <img src={item.images[imgIdx]} alt={item.title} className="sc-act-modal__img" />
              {item.images.length > 1 && (
                <>
                  <button className="sc-act-modal__nav sc-act-modal__nav--prev" onClick={e => { e.stopPropagation(); prev() }} aria-label="Previous image">
                    <ChevronLeft size={20} />
                  </button>
                  <button className="sc-act-modal__nav sc-act-modal__nav--next" onClick={e => { e.stopPropagation(); next() }} aria-label="Next image">
                    <ChevronRight size={20} />
                  </button>
                  <div className="sc-act-modal__dots">
                    {item.images.map((_, i) => (
                      <button
                        key={i}
                        className={`sc-act-modal__dot ${i === imgIdx ? 'sc-act-modal__dot--active' : ''}`}
                        onClick={e => { e.stopPropagation(); setImgIdx(i) }}
                        aria-label={`Image ${i + 1}`}
                      />
                    ))}
                  </div>
                  <div className="sc-act-modal__counter">{imgIdx + 1} / {item.images.length}</div>
                </>
              )}
            </>
          ) : (
            <div className="sc-act-modal__mock-icon">
              <Icon size={48} strokeWidth={1} />
            </div>
          )}
        </div>

        {/* Content */}
        <div className="sc-act-modal__body">
          {item.category && <span className="sc-act-modal__tag">{item.category}</span>}
          <h3 className="sc-act-modal__title">{item.title}</h3>
          {item.description && <p className="sc-act-modal__desc">{item.description}</p>}
        </div>
      </div>
    </div>
  )
}

function useVisible() {
  const w = useSyncExternalStore(
    cb => { window.addEventListener('resize', cb); return () => window.removeEventListener('resize', cb) },
    () => window.innerWidth,
    () => 1024,
  )
  if (w >= 1024) return 4
  if (w >= 640)  return 3
  return 2
}

export default function Activities({ activities }) {
  const list    = activities?.length ? activities : MOCK
  const visible = useVisible()
  const [offset, setOffset] = useState(0)
  const [activeItem, setActiveItem] = useState(null)
  const hovered = useRef(false)

  const max  = Math.max(0, list.length - visible)
  const cols = visible  // alias for translateX calc
  const prev = () => setOffset(o => Math.max(0, o - 1))
  const next = () => setOffset(o => Math.min(max, o + 1))

  useEffect(() => { setOffset(o => Math.min(o, max)) }, [max])

  useEffect(() => {
    if (list.length <= visible) return
    const id = setInterval(() => {
      if (hovered.current) return
      setOffset(o => (o >= max ? 0 : o + 1))
    }, 4500)
    return () => clearInterval(id)
  }, [max, list.length, visible])

  return (
    <>
      <section className="sc-section">
        <div className="sc-activities__header">
          <div>
            <h2 className="ljn-section-title">Activities &amp; Experiences</h2>
            <div className="ljn-divider" style={{ marginBottom: 0 }} />
          </div>
          {list.length > visible && (
            <div className="sc-activities__nav">
              <button className="sc-activities__btn" onClick={prev} disabled={offset === 0} aria-label="Previous">
                <ChevronLeft size={18} strokeWidth={1.5} />
              </button>
              <button className="sc-activities__btn" onClick={next} disabled={offset >= max} aria-label="Next">
                <ChevronRight size={18} strokeWidth={1.5} />
              </button>
            </div>
          )}
        </div>

        <div
          className="sc-activities__track-wrap"
          onMouseEnter={() => { hovered.current = true }}
          onMouseLeave={() => { hovered.current = false }}
        >
          <div
            className="sc-activities__track"
            style={{ transform: `translateX(calc(-${offset} * ((100% + 10px) / ${cols})))` }}
          >
            {list.map((item, i) => {
              const Icon = CATEGORY_ICONS[item.category] ?? Anchor
              const hasImg = item.images?.length > 0
              return (
                <div
                  key={i}
                  className="sc-activity-card"
                  onClick={() => setActiveItem({ item, colorIdx: i })}
                >
                  <div
                    className="sc-activity-card__img"
                    style={{ background: hasImg ? undefined : MOCK_COLORS[i % MOCK_COLORS.length] }}
                  >
                    {hasImg
                      ? <img src={item.images[0]} alt={item.title} />
                      : <div className="sc-activity-card__mock-icon"><Icon size={26} strokeWidth={1} /></div>
                    }
                  </div>
                  <div className="sc-activity-card__body">
                    <span>{item.category}</span>
                    <h4>{item.title}</h4>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {activeItem && (
        <ActivityModal
          item={activeItem.item}
          colorIdx={activeItem.colorIdx}
          onClose={() => setActiveItem(null)}
        />
      )}
    </>
  )
}
